// @ts-nocheck: converted from JS

import Button from '@/components/UI/Button';
import DatePickerNative from '@/components/UI/DatePickerNative';
import Radio from '@/components/UI/FormElements/Radio';
import Icon from '@/components/UI/Icon';
import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { validateFilter } from '@/helpers';
import { _isEmpty, _isEmptyString, _notNil } from '@/littledash';
import { DateUtils } from '@/utils/Date.utils';
import { type FC, useEffect, useState } from 'react';
import DateRangePicker from '../../DateRangePicker/DateRangePicker';
import MenuContainer from './MenuContainer';
import Select from '@/components/UI/Select';
import FilterLookup from './FilterLookup';
import { TimepointInput } from '@/components/UI/Timepoint/TimepointInput';
import styles from './TableFilterMenu.module.scss';

const EMPTY_OPTION = {
  name: '',
  value: '',
};

const operationHasOptions = (options) => options && !_isEmpty(options);

/**
 * Normalizes filters to ensure they have valid indices for filterOptions lookup.
 * Filters stored in views only contain value/name, but the component needs index
 * properties to work with native select elements.
 */
export const normalizeFilters = (filters, filterOptions, initialiseFilter) => {
  if (_isEmpty(filters) || _isEmpty(filterOptions)) {
    return [initialiseFilter()];
  }

  const normalized = filters.reduce((acc, filter) => {
    const category = filter.category;
    if (!category) {
      return acc;
    }

    // Find category index by value (stable identifier) or name as fallback
    const categoryIndex = filterOptions.findIndex((opt) => opt.value === category.value || opt.name === category.name);

    if (categoryIndex === -1) {
      // Category no longer exists in filterOptions - skip this filter
      return acc;
    }

    const matchedCategory = filterOptions[categoryIndex];
    const operations = matchedCategory.operations ?? [];
    const operation = filter.operation;

    // Find operation index by name, default to 0 if not found
    let operationIndex = 0;
    if (operation?.name && operations.length > 0) {
      const foundIndex = operations.findIndex((op) => op.name === operation.name);
      if (foundIndex !== -1) {
        operationIndex = foundIndex;
      }
    }

    // Rebuild filter with correct indices
    acc.push({
      ...filter,
      category: {
        ...category,
        value: matchedCategory.value ?? matchedCategory.name,
        name: matchedCategory.name,
        index: categoryIndex,
      },
      operation: {
        ...operation,
        index: operationIndex,
      },
    });

    return acc;
  }, []);

  // If all filters were invalid, return a fresh filter
  return normalized.length > 0 ? normalized : [initialiseFilter()];
};

interface TableFilterMenuProps {
  closeMenu?: () => void;
  filters?: Record<string, any>;
  filterMatch?: boolean;
  filterOptions: Array<{ name: string; value: string }>;
  onUpdateFilters: (filters: TableFilterMenuProps['filters'], filterMatch: boolean) => void;
}

const TableFilterMenu: FC<TableFilterMenuProps> = ({
  closeMenu,
  filters,
  filterMatch,
  filterOptions,
  onUpdateFilters,
}) => {
  const initialiseFilter = () => {
    const firstCategory = filterOptions[0];
    const firstOperation = firstCategory?.operations?.[0];
    const hasOptions = operationHasOptions(firstOperation?.options);
    if (firstCategory) {
      const filter = {
        category: {
          name: firstCategory.name,
          value: String(firstCategory.value || firstCategory.name),
          index: 0,
        },
        operation: {
          name: firstOperation?.name,
          index: 0,
        },
      };
      if (firstOperation?.name === filterType.between || firstOperation?.name === filterType.between_interval) {
        filter.option = { from: '', to: '' };
      } else if (hasOptions) {
        const firstOption = firstOperation?.options?.[0];
        filter.option = {
          name: firstOption?.name,
          value: firstOption?.value ?? firstOption?.name,
        };
      } else {
        filter.option = EMPTY_OPTION;
      }
      return filter;
    }
    return {};
  };

  const [localFilters, setLocalFilters] = useState(() => normalizeFilters(filters, filterOptions, initialiseFilter));
  const [localFilterMatch, setLocalFilterMatch] = useState(filterMatch);

  // Re-sync when filters prop changes (e.g., view selected)
  useEffect(() => {
    setLocalFilters(normalizeFilters(filters, filterOptions, initialiseFilter));
  }, [filters, filterOptions]);

  const addFilterRow = (e) => {
    setLocalFilters([...localFilters, initialiseFilter()]);
  };
  const removeFilterRow = (index) => {
    const updatedFilters = [...localFilters];
    updatedFilters.splice(index, 1);
    setLocalFilters(updatedFilters);
  };

  const onChangeFilterBetweenOptions = (selectedDays, index) => {
    const updatedFilters = [...localFilters];
    const from = DateUtils.isValidDate(selectedDays?.from) ? selectedDays.from : DateUtils.dateNow();
    const to = DateUtils.isValidDate(selectedDays?.to) ? selectedDays.to : DateUtils.dateNow();

    updatedFilters[index] = {
      ...updatedFilters[index],
      option: Date.parse(from) <= Date.parse(to) ? { from, to } : { from: to, to: from },
    };
    setLocalFilters(updatedFilters);
  };

  const onChangeFilterOptionText = (event, index, value) => {
    const updatedFilters = [...localFilters];

    if (_notNil(value) && (_isEmptyString(value.label, false) || _isEmptyString(value.value, false))) {
      updatedFilters[index] = {
        ...updatedFilters[index],
        option: {
          name: '',
          value: '',
        },
      };
    } else {
      updatedFilters[index] = {
        ...updatedFilters[index],
        option: {
          name: value?.label || event?.target?.name || '',
          value: value?.value || event?.target?.value || '',
        },
      };
    }

    setLocalFilters(updatedFilters);
  };
  const onChangeFilterOptionDate = (date, index) => {
    const updatedFilters = [...localFilters];
    const to = DateUtils.isValidDate(date) ? date : DateUtils.dateNow();

    updatedFilters[index] = {
      ...updatedFilters[index],
      option: {
        value: to,
      },
    };
    setLocalFilters(updatedFilters);
  };

  /* **
    This is triggered when the category is changed.
    When this happens, the operation and option are regenerated too.
  ***/
  const onChangeFilterCategory = (event, rowIndex) => {
    const currentCategoryIndex = event.target.value;
    const updatedCategory = {
      name: event.nativeEvent.target[event.nativeEvent.target.selectedIndex].text,
      value: event.nativeEvent.target[event.nativeEvent.target.selectedIndex].id,
      index: currentCategoryIndex,
    };

    const updatedOperation = filterOptions[currentCategoryIndex]?.operations[0];
    const hasOptions = operationHasOptions(filterOptions[currentCategoryIndex]?.operations?.[0]?.options);

    let updatedOption;
    if (updatedOperation.name === filterType.between || updatedOperation.name === filterType.between_interval) {
      updatedOption = hasOptions
        ? filterOptions[currentCategoryIndex]?.operations?.[0].options?.[0]
        : { from: '', to: '' };
    } else {
      const option = hasOptions ? filterOptions[currentCategoryIndex]?.operations?.[0].options?.[0] : '';
      updatedOption = hasOptions
        ? {
            name: option.name,
            value: option.value || option.name,
          }
        : EMPTY_OPTION;
    }
    const updatedFilters = [...localFilters];
    updatedFilters[rowIndex] = {
      category: updatedCategory,
      operation: {
        name: updatedOperation.name,
        index: 0,
      },
      option: updatedOption,
    };
    setLocalFilters(updatedFilters);
  };
  /** *
   This is triggered when the operation is changed.
   When this happens, the options are regenerated too.
   ***/
  const onChangeFilterOperation = (event, index) => {
    const currentCategoryIndex = localFilters[index].category.index;
    const currentOperationIndex = event.target.value;

    const updatedOperation = {
      name: event.nativeEvent.target[event.nativeEvent.target.selectedIndex].text,
      index: event.target.value,
    };

    const hasOptions = operationHasOptions(
      filterOptions[currentCategoryIndex].operations[currentOperationIndex].options
    );

    let updatedOption;
    if (updatedOperation.name === filterType.between_interval) {
      updatedOption = { from: '', to: '' };
    } else if (hasOptions) {
      const option = filterOptions[currentCategoryIndex].operations[currentOperationIndex].options[0];
      updatedOption = {
        name: option.name,
        value: option.value ? option.value : option.name,
      };
    } else {
      updatedOption = EMPTY_OPTION;
    }

    const updatedFilters = [...localFilters];
    updatedFilters[index] = {
      category: updatedFilters[index].category,
      operation: updatedOperation,
      option: updatedOption,
    };
    setLocalFilters(updatedFilters);
  };
  const onChangeFilterMatchAny = () => {
    setLocalFilterMatch(false);
  };
  const onChangeFilterMatchAll = () => {
    setLocalFilterMatch(true);
  };
  const onChangeFilterOptionSelect = (event, index) => {
    const updatedFilters = [...localFilters];
    updatedFilters[index] = {
      ...updatedFilters[index],
      option: {
        name: event.nativeEvent.target[event.nativeEvent.target.selectedIndex].text,
        value: event.target.value,
      },
    };
    setLocalFilters(updatedFilters);
  };

  const saveFilters = (e) => {
    e.preventDefault();
    onUpdateFilters(localFilters, localFilterMatch);
    closeMenu();
  };

  const clearFilters = (e) => {
    setLocalFilters([initialiseFilter()]);
    closeMenu();
  };

  const renderInput = (
    operation: any,
    filter: any,
    index: number,
    fetchFromApi?: FetchFromApiConfig,
    category?: any
  ) => {
    switch (operation.value) {
      case filterType.eq:
      case filterType.ne:
        if (_notNil(fetchFromApi)) {
          return (
            <FilterLookup
              fetchFromApi={fetchFromApi}
              value={filter.option}
              onSelect={(data) => onChangeFilterOptionText({}, index, data)}
            />
          );
        } else {
          const selectOptions = (operation.options || []).map((opt) => ({
            label: opt.name,
            value: opt.value || opt.name,
          }));

          const selectedOption =
            filter.option.value && filter.option.value !== ''
              ? selectOptions.find((opt) => opt.value === filter.option.value) || null
              : null;

          return (
            <div onClick={(e) => e.stopPropagation()} className="w-100" data-testid="filter-options-select">
              <Select
                options={selectOptions}
                value={selectedOption}
                onChange={(selectedOption) => {
                  if (_notNil(selectedOption)) {
                    onChangeFilterOptionText({}, index, {
                      label: selectedOption.label,
                      value: selectedOption.value,
                    });
                  } else {
                    onChangeFilterOptionText({}, index, {
                      label: '',
                      value: '',
                    });
                  }
                }}
                isMulti={false}
                placeholder="Type to choose..."
                isClearable={false}
                isSearchable={true}
              />
            </div>
          );
        }
      case filterType.in:
      case filterType.not_in:
        if (_notNil(fetchFromApi)) {
          return (
            <FilterLookup
              fetchFromApi={fetchFromApi}
              value={filter.option}
              onSelect={(data) => onChangeFilterOptionText({}, index, data)}
            />
          );
        } else {
          return (
            <select
              onChange={(event) => onChangeFilterOptionSelect(event, index)}
              name={filter.option.name}
              value={filter.option.value}
              className="f6 mb0"
              data-testid="option-input"
            >
              {operation.options.map((option) => (
                <option key={option.value} value={option.value} data-testid="option">
                  {option.name}
                </option>
              ))}
            </select>
          );
        }
      case filterType.contains:
      case filterType.not_contains:
        return (
          <input
            type="text"
            data-testid="option-input"
            className={`f6 ${styles.filterInput}`}
            onChange={(event) => onChangeFilterOptionText(event, index)}
            name={filter.option.name}
            value={filter.option.value}
            placeholder={'Enter value'}
            autoFocus
          />
        );
      case filterType.eq_interval:
      case filterType.gt_interval:
      case filterType.lt_interval:
        return (
          <div className="w-100" data-testid="option-input">
            <TimepointInput
              value={filter.option.value}
              placeholder="e.g. 1h"
              onChange={({ value }) => {
                const updatedFilters = [...localFilters];
                updatedFilters[index] = {
                  ...updatedFilters[index],
                  option: {
                    name: value || '',
                    value: value || '',
                  },
                };
                setLocalFilters(updatedFilters);
              }}
            />
          </div>
        );
      case filterType.gt:
      case filterType.lt:
        return (
          <input
            type="number"
            data-testid="option-input"
            className={`f6 ${styles.filterInput}`}
            onChange={(event) => onChangeFilterOptionText(event, index)}
            onWheel={(event) => event.target.blur()}
            name={filter.option.name}
            value={filter.option.value}
            placeholder={'Enter value'}
            autoFocus
          />
        );
      case filterType.between:
        return (
          <div className={styles.dateRangeCell}>
            <DateRangePicker
              range={filter.option}
              onChange={(selectedDays) => onChangeFilterBetweenOptions(selectedDays, index)}
            />
          </div>
        );
      case filterType.between_interval:
        return (
          <div className="flex items-center w-100">
            <div className="flex-auto" data-testid="option-input-from">
              <TimepointInput
                value={filter.option?.from || ''}
                placeholder="e.g. 1h"
                onChange={({ value }) => {
                  const updatedFilters = [...localFilters];
                  updatedFilters[index] = {
                    ...updatedFilters[index],
                    option: {
                      ...updatedFilters[index].option,
                      from: value || '',
                    },
                  };
                  setLocalFilters(updatedFilters);
                }}
              />
            </div>
            <span className="mh2 flex-shrink-0">to</span>
            <div className="flex-auto" data-testid="option-input-to">
              <TimepointInput
                value={filter.option?.to || ''}
                placeholder="e.g. 24h"
                onChange={({ value }) => {
                  const updatedFilters = [...localFilters];
                  updatedFilters[index] = {
                    ...updatedFilters[index],
                    option: {
                      ...updatedFilters[index].option,
                      to: value || '',
                    },
                  };
                  setLocalFilters(updatedFilters);
                }}
              />
            </div>
          </div>
        );
      case filterType.not_eq_date:
      case filterType.eq_date:
        return (
          <DatePickerNative
            data-testid="option-input"
            value={filter.option.value}
            onChange={(event) => {
              onChangeFilterOptionDate(event, index);
            }}
            style={{ marginBottom: 0 }}
          />
        );
    }
  };
  const preventDefaultForm = (event) => {
    event.preventDefault();
  };
  const renderFilterRow = (filterOptions, filter, rowIndex) => {
    if (!validateFilter(filter)) {
      return;
    }
    const currentCategory = filter.category.index;
    const currentOperation = filter.operation.index;

    const isBetween = filter.operation.name === filterType.between;
    const isBetweenInterval = filter.operation.name === filterType.between_interval;
    return (
      <div
        className="flex items-center mb2"
        key={`${filter.category.name}-${rowIndex}`}
        data-testid={`row-${rowIndex}`}
      >
        <select
          className="f6 mr2 mb0 w-30"
          onChange={(event) => onChangeFilterCategory(event, rowIndex)}
          value={currentCategory}
          data-testid="category-select"
        >
          {filterOptions.map((category, categoryIndex) => (
            <option
              key={category.name}
              value={categoryIndex}
              id={category.value || category.name}
              name={category.name}
              data-testid="category"
            >
              {category.name}
            </option>
          ))}
        </select>

        {!isBetween && (
          <select
            className="f6 mr2 mb0 w-25"
            onChange={(event) => onChangeFilterOperation(event, rowIndex)}
            name={filter.operation.name}
            value={currentOperation}
            data-testid="operation-select"
          >
            {filterOptions[currentCategory].operations.map((operation, operationIndex) => (
              <option key={operation.name} value={operationIndex} name={operation.name} data-testid="operation">
                {operation.name}
              </option>
            ))}
          </select>
        )}

        <div className={`f6 mr2 flex items-center ${isBetween || isBetweenInterval ? 'w-two-thirds' : 'w-40'}`}>
          {renderInput(
            filterOptions[currentCategory].operations[currentOperation],
            filter,
            rowIndex,
            filterOptions[currentCategory]?.fetchFromApi
          )}
        </div>
        <div className="flex-shrink-0 flex items-center justify-center">
          <Button
            testId={`remove-row-button-${rowIndex}`}
            aria-label="Remove filter"
            className="hover-near-black repeater-field__remove-btn soft"
            onClick={() => removeFilterRow(rowIndex)}
          >
            <Icon icon="close" width="10" height="10" className="dark-gray" />
          </Button>
        </div>
      </div>
    );
  };

  if (_isEmpty(filters) && _isEmpty(filterOptions)) {
    return null;
  }

  return (
    <MenuContainer width={720}>
      <form onSubmit={preventDefaultForm}>
        <div className="pa3">
          <div className="mb3">
            <div className="f6 near-black lh-title pb2">Results must match</div>
            <div className="flex flex-wrap">
              <Radio
                checked={localFilterMatch}
                onChange={onChangeFilterMatchAll}
                id="all"
                label="All conditions"
                data-testid="match-all-button"
                className="mr3"
              />
              <Radio
                checked={!localFilterMatch}
                onChange={onChangeFilterMatchAny}
                id="any"
                label="Any condition"
                data-testid="match-any-button"
                className="mr3"
              />
            </div>
          </div>
          {localFilters.map((filter, index) => renderFilterRow(filterOptions, filter, index))}
          <Button onClick={addFilterRow} soft blue className="mr3 f6" testId="add-row-button">
            Add condition
          </Button>
        </div>
        <div className="pa3 bt b--moon-gray">
          <Button type="submit" className="mr2 f6" onClick={saveFilters} testId="save-button">
            Filter
          </Button>
          <Button type="submit" className="plain f6" onClick={clearFilters} testId="cancel-button">
            Cancel
          </Button>
        </div>
      </form>
    </MenuContainer>
  );
};

export default TableFilterMenu;
